import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import NotificationPanel from '@/components/NotificationPanel';
import ModuleBadge from '@/components/ModuleBadge';
import { useModuleBadges, notifyBadgesChanged, BADGES_REFRESH_EVENT } from '@/lib/moduleBadges';
import { useReceptionAccess } from '@/lib/receptionAccess';
import BonusCelebration from '@/components/BonusCelebration';
import BirthdayCelebration from '@/components/BirthdayCelebration';
import Announcements from '@/components/Announcements';
import { 
  LayoutDashboard, 
  User, 
  MessageSquare, 
  LogOut,
  Globe,
  Bell,
  Eye,
  X,
  Shield,
  Droplets,
  Package,
  Calendar,
  ClipboardList,
  FolderKanban,
  Users,
  TrendingUp,
  ClipboardCheck,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MainLayout = ({ children }) => {
  const { user, realUser, logout, viewMode, setViewMode, isPreviewMode } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [employees, setEmployees] = useState([]);
  const moduleBadges = useModuleBadges();
  const location = useLocation();
  // Messages ouvert : la barre du bas mobile disparait pour laisser la place au fil
  const chatOpen = location.pathname.startsWith('/chat');

  const isRealManager = realUser?.role === 'manager';

  const txt = (fr, ru, md, uk) => ({ fr, ru, md, uk }[language] || fr);

  // Fetch employees for view-as dropdown (manager only)
  useEffect(() => {
    if (!isRealManager) return;
    const fetchEmployees = async () => {
      try {
        const res = await axios.get(`${API}/users`);
        const emps = (res.data || []).filter(u => u.role === 'employee' || u.role === 'subcontractor');
        setEmployees(emps);
      } catch (e) {
        console.error('Failed to fetch employees:', e);
      }
    };
    fetchEmployees();
  }, [isRealManager]);

  // Get company name to display based on user role
  const getCompanyName = () => {
    if (!user) return t('appName');
    
    // In preview mode, show the employee's company
    if (isPreviewMode && viewMode) {
      return viewMode.company === 'Eco Bati Construction' ? 'Eco Bati Construction' : 'La Piscine Container';
    }
    
    // Manager sees both companies
    if (user.role === 'manager') {
      return 'LPC / EBC';
    }
    
    // Employee sees their company name
    if (user.company === 'Eco Bati Construction') {
      return 'Eco Bati Construction';
    }
    return 'La Piscine Container';
  };

  const isSubcontractor = user?.role === 'subcontractor';
  const navigate = useNavigate();

  // Periode d'essai (Pavel 12/10/2026) : l'employe en essai est limite au pointage.
  // Menu reduit, autres pages redirigees vers /attendance, bandeau d'info en haut.
  const pointageOnly = !!user?.pointage_only && user?.role !== 'manager';
  const trialAllowed = ['/attendance', '/profile'];
  useEffect(() => {
    if (pointageOnly && !trialAllowed.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
      navigate('/attendance', { replace: true });
    }
  }, [pointageOnly, location.pathname]);

  // Messages en tete des modules (Pavel 2026-08-02), pastilles rouges par module
  // Onglet Reception : manager toujours, employe seulement s'il participe a une reception
  const receptionAccess = useReceptionAccess(isRealManager);
  const navItems = [
    ...(!isSubcontractor ? [{ path: '/', icon: LayoutDashboard, labelKey: 'dashboard' }] : []),
    ...(!isSubcontractor ? [{ path: '/chat', icon: MessageSquare, label: txt('Messages', 'Сообщения', 'Mesaje', 'Повідомлення'), moduleKey: 'chat' }] : []),
    ...(!isSubcontractor && !(isRealManager && !isPreviewMode) ? [{ path: '/attendance', icon: Calendar, label: txt('Pointage', 'Посещение', 'Pontaj', 'Відвідування'), moduleKey: 'attendance' }] : []),
    ...(!isSubcontractor ? [{ path: '/projects', icon: FolderKanban, label: txt('Chantiers', 'Проекты', 'Șantiere', 'Проекти'), moduleKey: 'projects' }] : []),
    ...(!isSubcontractor ? [{ path: '/materials', icon: Package, label: txt('Matériaux', 'Материалы', 'Materiale', 'Матеріали'), moduleKey: 'materials' }] : []),
    ...(!isSubcontractor && receptionAccess ? [{ path: '/reception', icon: ClipboardCheck, label: txt('Réception', 'Приёмка', 'Recepție', 'Приймання'), moduleKey: 'reception' }] : []),
    ...(isRealManager && !isPreviewMode ? [{ path: '/team-attendance', icon: Users, label: txt('Équipe', 'Команда', 'Echipă', 'Команда'), moduleKey: 'team' }] : []),
    ...(isRealManager && !isPreviewMode ? [{ path: '/stats', icon: TrendingUp, label: txt('Statistiques', 'Статистика', 'Statistici', 'Статистика') }] : []),
    ...(isSubcontractor || (isRealManager && !isPreviewMode) ? [{ path: '/missions', icon: ClipboardList, label: 'Missions' }] : []),
    ...(isSubcontractor ? [{ path: '/maintenance', icon: Droplets, label: txt('Entretien', 'Обслуживание', 'Întreținere', 'Обслуговування') }] : []),
    ...(isSubcontractor ? [{ path: '/materials', icon: Package, label: txt('Matériaux', 'Материалы', 'Materiale', 'Матеріали'), moduleKey: 'materials' }] : []),
    { path: '/profile', icon: User, labelKey: 'profile' },
  ];

  // En periode d'essai, on ne montre que Pointage et Profil.
  const visibleNavItems = pointageOnly
    ? navItems.filter(i => i.path === '/attendance' || i.path === '/profile')
    : navItems;

  const languageFlags = {
    fr: '🇫🇷',
    ru: '🇷🇺',
    md: '🇲🇩',
    uk: '🇺🇦'
  };

  // En mode apercu, la cloche montre le compteur du salarie selectionne
  // (param explicite : ne depend pas du timing de l'intercepteur axios)
  const notifCountParams = viewMode?.id && realUser?.role === 'manager' ? { view_as: viewMode.id } : {};

  // Fetch unread notification count
  useEffect(() => {
    let isMounted = true;

    const fetchNotifCount = async () => {
      try {
        const response = await axios.get(`${API}/notifications/unread-count`, { params: notifCountParams });
        if (isMounted) {
          setUnreadNotifCount(response.data.count || 0);
        }
      } catch (error) {
        console.error('Failed to fetch notification count:', error);
      }
    };

    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 30000);
    // Ouvrir un module (ou lire dans le panneau) resynchronise la cloche immediatement
    window.addEventListener(BADGES_REFRESH_EVENT, fetchNotifCount);

    return () => {
      isMounted = false;
      clearInterval(interval);
      window.removeEventListener(BADGES_REFRESH_EVENT, fetchNotifCount);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode?.id, realUser?.role]);

  const handleNotifPanelClose = () => {
    setShowNotifPanel(false);
    // Refresh count after closing
    axios.get(`${API}/notifications/unread-count`, { params: notifCountParams })
      .then(res => setUnreadNotifCount(res.data.count || 0))
      .catch(err => console.error('Failed to refresh notification count:', err));
    // Les lectures faites dans le panneau effacent aussi les pastilles de modules
    notifyBadgesChanged();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      {/* Desktop Sidebar */}
      <aside className="sidebar hidden lg:flex flex-col relative">
        <div className="p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-3">
            <img src="/logo-piscine.png" alt="Logo" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-lg font-bold tracking-tight" style={{ color: 'var(--color-primary)' }}>
                {getCompanyName()}
              </h1>
              <p className="text-xs" style={{ color: 'var(--color-text)', opacity: 0.6 }}>{t('appSubtitle')}</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {visibleNavItems.map(item => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) => 
                    `flex items-center gap-3 px-4 py-3 font-medium transition-colors relative ${
                      isActive 
                        ? 'bg-[#0E2147]/10 text-[#0E2147]' 
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                  style={{ borderRadius: 'var(--radius-sm)' }}
                  data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                >
                  <span className="relative">
                    <item.icon className="w-5 h-5" strokeWidth={2} />
                    <ModuleBadge count={moduleBadges[item.moduleKey]} />
                  </span>
                  <span>{item.label || t(item.labelKey)}</span>
                </NavLink>
              </li>
            ))}
            
            {/* Notifications Button */}
            <li className="relative">
              <button
                onClick={() => setShowNotifPanel(!showNotifPanel)}
                className="sidebar-link w-full flex items-center gap-3 px-4 py-3 font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                style={{ borderRadius: 'var(--radius-sm)' }}
                data-testid="desktop-notif-btn"
              >
                <div className="relative">
                  <Bell className="w-5 h-5" strokeWidth={2} />
                  {unreadNotifCount > 0 && (
                    <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
                      {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                    </span>
                  )}
                </div>
                <span>{t('notifications')}</span>
              </button>
              {/* Desktop Notification Panel - positioned relative to this button */}
              {showNotifPanel && (
                <NotificationPanel isOpen={showNotifPanel} onClose={handleNotifPanelClose} />
              )}
            </li>
          </ul>
        </nav>

        {/* View Mode Switcher (Manager only) */}
        {isRealManager && (
          <div className="px-4 pb-2">
            <DropdownMenu>
              <DropdownMenuTrigger 
                className={`flex items-center gap-3 px-4 py-3 w-full text-left font-medium transition-colors ${
                  isPreviewMode 
                    ? 'bg-amber-50 text-amber-700 border border-amber-200' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`} 
                style={{ borderRadius: 'var(--radius-sm)' }}
                data-testid="view-mode-switcher"
              >
                {isPreviewMode ? <Eye className="w-5 h-5" strokeWidth={2} /> : <Shield className="w-5 h-5" strokeWidth={2} />}
                <span className="flex-1 text-sm truncate">
                  {isPreviewMode 
                    ? viewMode?.name || txt('Mode employé', 'Режим сотрудника', 'Mod angajat', 'Режим працівника')
                    : txt('Mode manager', 'Режим менеджера', 'Mod manager', 'Режим менеджера')
                  }
                </span>
                {isPreviewMode && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 flex-shrink-0">
                    {viewMode?.company === 'Eco Bati Construction' ? 'EBC' : 'LPC'}
                  </span>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 max-h-80 overflow-y-auto">
                <DropdownMenuItem 
                  onClick={() => setViewMode(null)} 
                  className={`cursor-pointer ${!isPreviewMode ? 'bg-blue-50 text-blue-700' : ''}`}
                >
                  <Shield className="w-4 h-4 mr-2" />
                  {txt('Manager (normal)', 'Менеджер (обычный)', 'Manager (normal)', 'Менеджер (звичайний)')}
                </DropdownMenuItem>
                {employees.length > 0 && (
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {txt('Voir comme...', 'Смотреть как...', 'Vezi ca...', 'Переглянути як...')}
                  </div>
                )}
                {employees.map(emp => (
                  <DropdownMenuItem 
                    key={emp.id}
                    onClick={() => setViewMode(emp)} 
                    className={`cursor-pointer ${viewMode?.id === emp.id ? 'bg-amber-50 text-amber-700' : ''}`}
                  >
                    <Eye className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="truncate flex-1">{emp.name}</span>
                    {emp.role === 'subcontractor' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 ml-1 flex-shrink-0">Sous-traitant</span>}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 ml-1 flex-shrink-0">
                      {emp.company === 'Eco Bati Construction' ? 'EBC' : 'LPC'}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Language Selector */}
        <div className="px-4 pb-2">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-3 px-4 py-3 w-full text-left font-medium text-gray-600 hover:bg-gray-50 transition-colors" style={{ borderRadius: 'var(--radius-sm)' }}>
              <Globe className="w-5 h-5" strokeWidth={2} />
              <span>{t('language')}</span>
              <span className="ml-auto text-lg">{languageFlags[language]}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem onClick={() => setLanguage('fr')} className="cursor-pointer">
                <span className="mr-2">🇫🇷</span> {t('french')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('ru')} className="cursor-pointer">
                <span className="mr-2">🇷🇺</span> {t('russian')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('md')} className="cursor-pointer">
                <span className="mr-2">🇲🇩</span> {t('moldovan')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('uk')} className="cursor-pointer">
                <span className="mr-2">🇺🇦</span> {t('ukrainian')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        <div className="p-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-3 px-4 py-2">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
              {user?.photo_url ? (
                <img src={user.photo_url} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <User className="w-5 h-5" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: 'var(--color-text)' }}>{user?.name}</p>
              <p className="text-xs truncate" style={{ color: 'var(--color-text)', opacity: 0.5 }}>{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-3 px-4 py-3 w-full text-left font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors mt-2"
            style={{ borderRadius: 'var(--radius-sm)' }}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" strokeWidth={2} />
            <span>{t('logout')}</span>
          </button>
        </div>
        
        {/* Desktop Notification Panel moved to sidebar nav item */}
      </aside>
      
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b z-50 mobile-header" style={{ borderColor: 'var(--color-border)', paddingTop: '0.5rem', paddingBottom: '0.5rem', paddingLeft: '1rem', paddingRight: '1rem' }}>
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img src="/logo-piscine.png" alt="Logo" className="w-8 h-8 object-contain" />
            <h1 className="text-lg font-bold tracking-tight" style={{ color: 'var(--color-primary)' }}>
              {getCompanyName()}
            </h1>
          </Link>
          <div className="flex items-center gap-2">
            {/* Mobile View Mode Switcher (Manager only) */}
            {isRealManager && (
              <DropdownMenu>
                <DropdownMenuTrigger 
                  className={`p-2 rounded-lg ${isPreviewMode ? 'bg-amber-100 text-amber-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  data-testid="mobile-view-mode-switcher"
                >
                  {isPreviewMode ? <Eye className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 max-h-72 overflow-y-auto">
                  <DropdownMenuItem onClick={() => setViewMode(null)} className={`cursor-pointer ${!isPreviewMode ? 'bg-blue-50' : ''}`}>
                    <Shield className="w-4 h-4 mr-2" /> Manager
                  </DropdownMenuItem>
                  {employees.length > 0 && (
                    <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {txt('Voir comme...', 'Смотреть как...', 'Vezi ca...', 'Переглянути як...')}
                    </div>
                  )}
                  {employees.map(emp => (
                    <DropdownMenuItem 
                      key={emp.id}
                      onClick={() => setViewMode(emp)} 
                      className={`cursor-pointer ${viewMode?.id === emp.id ? 'bg-amber-50' : ''}`}
                    >
                      <Eye className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate flex-1">{emp.name}</span>
                      {emp.role === 'subcontractor' && <span className="text-[10px] px-1 rounded bg-amber-100 text-amber-600 ml-1">ST</span>}
                      <span className="text-[10px] px-1 rounded bg-gray-100 text-gray-500 ml-1">
                        {emp.company === 'Eco Bati Construction' ? 'EBC' : 'LPC'}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {/* Mobile Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger className="p-2 text-gray-600 hover:bg-gray-100" style={{ borderRadius: 'var(--radius-sm)' }}>
                <span className="text-lg">{languageFlags[language]}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLanguage('fr')} className="cursor-pointer">
                  <span className="mr-2">🇫🇷</span> FR
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('ru')} className="cursor-pointer">
                  <span className="mr-2">🇷🇺</span> RU
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('md')} className="cursor-pointer">
                  <span className="mr-2">🇲🇩</span> MD
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('uk')} className="cursor-pointer">
                  <span className="mr-2">🇺🇦</span> UK
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Notification Bell */}
            <button
              onClick={() => setShowNotifPanel(!showNotifPanel)}
              className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              data-testid="notif-bell-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadNotifCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                  {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                </span>
              )}
            </button>
            
            <NavLink to="/profile" className="w-8 h-8 rounded-full overflow-hidden bg-gray-200">
              {user?.photo_url ? (
                <img src={user.photo_url} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <User className="w-4 h-4" />
                </div>
              )}
            </NavLink>
          </div>
        </div>
        
        {/* Mobile Notification Panel - appears below header */}
        {showNotifPanel && (
          <div className="absolute top-full left-0 right-0 bg-white border-b shadow-lg z-[100]" style={{ borderColor: 'var(--color-border)' }}>
            <NotificationPanel isOpen={showNotifPanel} onClose={handleNotifPanelClose} isMobile={true} />
          </div>
        )}
      </header>
      
      {/* Main Content */}
      <main className={`main-content lg:pt-0${chatOpen ? ' chat-open' : ''}`} style={{ paddingTop: '3.5rem' }}>
        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <div className="sticky top-0 z-40 bg-amber-500 text-white px-4 py-2 flex items-center justify-between" data-testid="preview-banner">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Eye className="w-4 h-4" />
              <span>
                {txt(
                  `Vue : ${viewMode?.name || 'Employé'}`,
                  `Просмотр: ${viewMode?.name || 'Сотрудник'}`,
                  `Vizualizare: ${viewMode?.name || 'Angajat'}`,
                  `Перегляд: ${viewMode?.name || 'Працівник'}`
                )}
              </span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-white/20">
                {viewMode?.company === 'Eco Bati Construction' ? 'EBC' : 'LPC'}
              </span>
            </div>
            <button 
              onClick={() => setViewMode(null)} 
              className="flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
              data-testid="exit-preview-btn"
            >
              <X className="w-4 h-4" />
              {txt('Retour manager', 'Назад к менеджеру', 'Înapoi manager', 'Назад до менеджера')}
            </button>
          </div>
        )}
        {pointageOnly && (
          <div className="bg-blue-600 text-white px-4 py-3 text-sm font-medium" data-testid="trial-banner">
            {txt(
              `Bienvenue ${user?.name?.split(' ')[0] || ''} ! Vous êtes en période d'essai d'un mois${user?.trial_until ? ` (jusqu'au ${new Date(user.trial_until).toLocaleDateString('fr-FR')})` : ''}. Pour le moment, vous pouvez uniquement pointer vos journées.`,
              `Добро пожаловать${user?.name ? ', ' + user.name.split(' ')[0] : ''}! У вас испытательный срок один месяц${user?.trial_until ? ` (до ${new Date(user.trial_until).toLocaleDateString('ru-RU')})` : ''}. Пока вы можете только отмечать посещаемость.`,
              `Bun venit${user?.name ? ', ' + user.name.split(' ')[0] : ''}! Sunteți în perioada de probă de o lună${user?.trial_until ? ` (până la ${new Date(user.trial_until).toLocaleDateString('ro-RO')})` : ''}. Deocamdată puteți doar să pontați.`,
              `Ласкаво просимо${user?.name ? ', ' + user.name.split(' ')[0] : ''}! У вас випробувальний термін один місяць${user?.trial_until ? ` (до ${new Date(user.trial_until).toLocaleDateString('uk-UA')})` : ''}. Поки що ви можете лише відмічати відвідуваність.`
            )}
          </div>
        )}
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
      
      {/* Mobile Bottom Navigation - masquee quand Messages est ouvert */}
      {!chatOpen && (
      <nav className="bottom-nav lg:hidden">
        <div className="bottom-nav-scroll flex items-center justify-start gap-2 px-2 overflow-x-auto">
          {visibleNavItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `nav-item relative ${isActive ? 'active' : ''}`}
              data-testid={`mobile-nav-${item.path.replace('/', '') || 'dashboard'}`}
            >
              <span className="relative">
                <item.icon className="w-5 h-5" strokeWidth={2} />
                <ModuleBadge count={moduleBadges[item.moduleKey]} className="-top-1.5 -right-2.5" />
              </span>
              <span className="text-xs font-medium">{item.label || t(item.labelKey)}</span>
            </NavLink>
          ))}
        </div>
      </nav>
      )}
      <BonusCelebration />
      <BirthdayCelebration />
      {/* Le bouton d'annonce a tout le personnel ne vit que sur le tableau de bord */}
      <Announcements user={user} isManager={isRealManager && !isPreviewMode} showFab={location.pathname === '/'} />
    </div>
  );
};

export default MainLayout;
